import dayjs from "dayjs";
import "dayjs/locale/es";

/**
 * Spanish date formatters for customer-facing printed documents.
 *
 * Module-local: importing "dayjs/locale/es" registers the locale without
 * changing dayjs's global default (dayjs/locale/es.js calls
 * `locale(def, null, true)`). Every call below chains `.locale("es")`
 * explicitly — never call `dayjs.locale(...)` globally, that would affect
 * every other dayjs consumer in the app.
 *
 * CRITICAL-2: `dayjs(iso)` on a full ISO timestamp (e.g. a `Z`-suffixed
 * backend value) converts to the BROWSER's local timezone before rendering,
 * which can move the printed calendar date by a day
 * ("2026-06-17T00:00:00Z" -> 16-jun-26 in America/Bogota). This codebase
 * already has a deliberate, timezone-immune convention for exactly this —
 * `utils.tsx` `formatDate` does `date.split("T")[0]` before ever touching a
 * Date/dayjs object. Both formatters below follow the same convention: take
 * only the calendar-date portion of the input and hand dayjs a date-only
 * string, which dayjs always parses as local midnight — a value that can
 * never disagree with itself across timezones because no UTC->local
 * conversion ever happens.
 */

/** Takes the calendar-date portion of an ISO string ("YYYY-MM-DD..." -> "YYYY-MM-DD"). */
const toCalendarDate = (iso: string): string => iso.split("T")[0];

/** e.g. "miércoles, 17 de junio de 2026" */
export const formatSpanishLongDate = (iso: string): string => {
  if (!iso) return "";

  return dayjs(toCalendarDate(iso)).locale("es").format("dddd, D [de] MMMM [de] YYYY");
};

/**
 * e.g. "17-jun-26" — dayjs' `es` locale monthsShort output as-is, day not
 * zero-padded. Decision #11: no local monthsShort table is built to add the
 * trailing period some print samples show (e.g. "jun."); the scan is a base,
 * not a pixel contract.
 */
export const formatSpanishShortDate = (iso: string): string => {
  if (!iso) return "";

  return dayjs(toCalendarDate(iso)).locale("es").format("D-MMM-YY");
};
